import {
  Box,
  Card,
  Icon,
  Flex,
  Text,
  Heading,
  FormLabel,
  IconButton,
  FormControl,
  CurrencyInput,
  FormHelperText
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'
import { formatCurrency } from '../../../../../../helpers/formatters'
import { NATIONAL_AVG_DOWN_PAYMENT } from '../EstimatedMonthlyPaymentBreakdown'

export const MSG = defineMessages({
  mortgageInsurance: {
    id: 'EstimatedMonthlyPaymentBreakdown.mortgageInsurance',
    defaultMessage: 'Mortgage insurance'
  },
  mortgageInsuranceDescription: {
    id: 'EstimatedMonthlyPaymentBreakdown.mortgageInsuranceDescription',
    defaultMessage: 'Since your down payment is {pctDown}%'
  },
  mortgageInsuranceShow: {
    id: 'EstimatedMonthlyPaymentBreakdown.mortgageInsuranceShow',
    defaultMessage: 'Show editable fields'
  },
  mortgageInsuranceHide: {
    id: 'EstimatedMonthlyPaymentBreakdown.mortgageInsuranceHide',
    defaultMessage: 'Hide editable fields'
  },
  monthlyAmount: {
    id: 'EstimatedMonthlyPaymentBreakdown.monthlyAmount',
    defaultMessage: 'Monthly amount'
  },
  monthlyAmountDesc: {
    id: 'EstimatedMonthlyPaymentBreakdown.monthlyAmountDesc',
    defaultMessage: 'Usually required if down payment is less than 20%'
  }
})

export interface EstimatedMonthlyPaymentBreakdownMortgageInsuranceProps {
  mortgageInsurance: string
  isCalculatorsPage?: boolean
  downPaymentPercentage: string
  setMortgageInsurance: (value: string) => void
  setHasUsedMortgageCalculator: (value: boolean) => void
}

export const EstimatedMonthlyPaymentBreakdownMortgageInsurance = ({
  isCalculatorsPage,
  mortgageInsurance,
  setMortgageInsurance,
  downPaymentPercentage,
  setHasUsedMortgageCalculator
}: EstimatedMonthlyPaymentBreakdownMortgageInsuranceProps) => {
  const intl = useIntl()
  const [showEditableFields, setShowEditableFields] = useState(false)

  return (
    <Card p={3}>
      <Flex justify='space-between' align='center'>
        <Flex gap={2} align='center'>
          <Box bg='primary.600' width={3} height={3} borderRadius='50%' />
          <Heading as='h4' size={['2xs', 'xs', 'sm']}>
            {intl.formatMessage(MSG.mortgageInsurance)}
          </Heading>
        </Flex>
        <Flex>
          <Text
            mb={0}
            color='neutral.900'
            size={['xs', 'sm', 'md']}
            fontWeight='semibold'
            style={{ fontVariant: 'tabular-nums' }}
          >
            {formatCurrency(mortgageInsurance)}
          </Text>
          <IconButton
            size='xs'
            variant='link'
            onClick={() => setShowEditableFields(!showEditableFields)}
            icon={<Icon height={2} name='play' transform={`rotate(${showEditableFields ? -90 : 90}deg)`} />}
            aria-label={intl.formatMessage(showEditableFields ? MSG.mortgageInsuranceHide : MSG.mortgageInsuranceShow)}
            tracking={{
              guid: 'sibyWYXBds3T9vv9Ae4qon',
              ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator',
              descriptor: `Mortgage Insurance ${showEditableFields ? 'Hide' : 'Show'}`
            }}
          />
        </Flex>
      </Flex>
      <Text ml={5} as='span' size={['2xs', 'xs', 'sm']} display='block'>
        {intl.formatMessage(MSG.mortgageInsuranceDescription, {
          pctDown:
            parseFloat(downPaymentPercentage)
              .toString()
              .replace(/(\.\d*?[1-9])0+$/, '$1') || NATIONAL_AVG_DOWN_PAYMENT * 100
        })}
      </Text>
      {showEditableFields && (
        <FormControl mt={4}>
          <FormLabel size='xs'>{intl.formatMessage(MSG.monthlyAmount)}</FormLabel>
          <CurrencyInput
            size='xs'
            maxWidth='250px'
            decimalScale={2}
            name='mortgageInsurance'
            value={mortgageInsurance}
            // @ts-ignore
            onChange={val => setMortgageInsurance(val)}
            onFocus={() => setHasUsedMortgageCalculator(true)}
            tracking={{
              guid: 'd6MiUrn81Edt2aeBa5tzKn',
              descriptor: 'Mortgage Insurance',
              ui_context: isCalculatorsPage ? 'Mortgage Calculator Page' : 'Listing Details Page Mortgage Calculator'
            }}
          />
          <FormHelperText>
            <Text mb={0} size='3xs'>
              {intl.formatMessage(MSG.monthlyAmountDesc)}
            </Text>
          </FormHelperText>
        </FormControl>
      )}
    </Card>
  )
}
